import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpand, faInfoCircle, faCalendarDay, faSpinner, faTrophy, faBullseye, faClock, faBookOpen, faFire, faBrain, faArrowUp, faArrowDown, faChartLine, faCalendarAlt, faBook, faCheckCircle, faTimesCircle, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { Link, useNavigate } from 'react-router-dom';
import './PerformanceHistory.css';
import Footer from './Footer';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import Modal from './Modal';
import { useTheme } from '../contexts/ThemeContext';
import examService from '../services/examService';
import examTypeService from '../services/examTypeService';
import progressReportService from '../services/progressReportService';
import categoryService from '../services/categoryService';

/**
 * PerformanceHistory Component
 * 
 * Optimizations:
 * - Uses the new /api/progressreport/performance-trend endpoint for optimized daily performance charts
 * - Uses the new /api/progressreport/performance-summary endpoint for optimized performance metrics
 * - Fallback to local calculations when API endpoints are unavailable
 * - Reduced redundant calculations by leveraging server-side aggregations
 * - Improved error handling and validation for API responses
 * - Better performance with large datasets through server-side processing
 */
export default function PerformanceHistory() {
  const navigate = useNavigate();
  
  // State for UI controls
  const [testType, setTestType] = useState('all');
  const [selectedExamTypeId, setSelectedExamTypeId] = useState(null);
  const [selectedExamType, setSelectedExamType] = useState('');
  const [category, setCategory] = useState('all');
  const [timeframe, setTimeframe] = useState('all');
  const [expandedChart, setExpandedChart] = useState(null);
  const { isDarkMode } = useTheme();
  
  // State for API data
  const [examTypes, setExamTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [examHistory, setExamHistory] = useState([]);
  const [performanceData, setPerformanceData] = useState([]);
  const [categoryReport, setCategoryReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Performance summary state
  const [performanceSummary, setPerformanceSummary] = useState({
    totalExams: 0,
    totalQuestions: 0,
    averageAccuracy: 0,
    totalStudyTime: 0,
    improvementTrend: 0,
    examsThisWeek: 0,
    bestDailyScore: 0,
    averageDailyQuestions: 0,
    strongestCategory: '',
    weakestCategory: ''
  });
  
  // The current date
  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Fetch exam types from API
  const fetchExamTypes = useCallback(async () => {
    try {
      const types = await examTypeService.getExamTypes();
      setExamTypes(types || []);
      
      // Set default exam type to first one or find CBSE
      if (types && types.length > 0) {
        const cbseType = types.find(type => type.name === 'CBSE');
        if (cbseType) {
          setSelectedExamTypeId(cbseType.id);
          setSelectedExamType(cbseType.name);
        } else {
          setSelectedExamTypeId(types[0].id);
          setSelectedExamType(types[0].name);
        }
      }
    } catch (err) {
      console.error('Failed to fetch exam types:', err);
      setError('Failed to load exam types');
    }
  }, []);

  // Fetch categories for the selected exam type
  const fetchCategories = useCallback(async () => {
    if (!selectedExamTypeId) return;
    
    try {
      const categories = await categoryService.getCategories({ ExamTypeId: selectedExamTypeId });
      setCategories(categories || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  }, [selectedExamTypeId]);

  // Process performance data for charts (fallback method when API endpoint fails)
  const processPerformanceData = useCallback((history) => {
    if (!history || history.length === 0) {
      setPerformanceData([]);
      return;
    }

    // Filter history by category if a specific category is selected
    let filteredHistory = history;
    if (category !== 'all') {
      // Note: This assumes exam history has category information
      // If not available, we might need to fetch additional data or modify the API
      filteredHistory = history.filter(exam => {
        // This filtering logic may need to be adjusted based on actual exam data structure
        // For now, we'll process all data if we can't filter by category at this level
        return true; // TODO: Implement proper category filtering when exam data includes category info
      });
    }

    // Sort history by date
    const sortedHistory = [...filteredHistory].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
    
    // Group by date for chart data
    const dailyData = {};
    
    sortedHistory.forEach(exam => {
      const date = new Date(exam.startDate).toLocaleDateString();
      const questions = exam.questionCount || 0;
      const responses = exam.responseCount || 0;
      const accuracy = questions > 0 ? (responses / questions) * 100 : 0;
      
      if (!dailyData[date]) {
        dailyData[date] = {
          date,
          totalQuestions: 0,
          totalCorrect: 0,
          examCount: 0,
          averageAccuracy: 0,
          totalTime: 0
        };
      }
      
      dailyData[date].totalQuestions += questions;
      dailyData[date].totalCorrect += Math.round((accuracy / 100) * questions);
      dailyData[date].examCount += 1;
      dailyData[date].totalTime += exam.timeSpent || 0;
    });
    
    // Calculate daily averages
    const chartData = Object.values(dailyData).map(day => ({
      name: day.date,
      totalQuestions: day.totalQuestions,
      correctAnswers: day.totalCorrect,
      incorrectAnswers: day.totalQuestions - day.totalCorrect,
      accuracy: day.totalQuestions > 0 ? Math.round((day.totalCorrect / day.totalQuestions) * 100) : 0,
      examCount: day.examCount,
      studyTime: Math.round(day.totalTime / 60) // Convert to minutes
    }));
    
    setPerformanceData(chartData);
  }, [category]);

  // Fetch performance trend using the optimized endpoint
  const fetchPerformanceTrend = useCallback(async () => {
    if (!selectedExamTypeId) return;
    
    try {
      const options = {
        ExamTypeId: selectedExamTypeId
      };
      
      // Add filters
      if (timeframe !== 'all') {
        const days = parseInt(timeframe);
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - days);
        
        options.StartDateFrom = startDate;
        options.StartDateTo = endDate;
      }
      
      if (testType !== 'all') {
        options.ExamMode = testType;
      }
      
      if (category !== 'all') {
        options.CategoryIds = [parseInt(category)];
      }

      const trendData = await progressReportService.getPerformanceTrend(options);
      
      // Transform API data to chart format
      const chartData = trendData.map(item => ({
        name: new Date(item.date).toLocaleDateString(),
        date: item.date,
        totalQuestions: item.totalQuestions,
        correctAnswers: item.totalCorrectAnswers,
        incorrectAnswers: item.totalIncorrectAnswers,
        accuracy: item.averageAccuracy,
        examCount: item.totalExams,
        studyTime: Math.round(item.totalStudyTimeSeconds / 60) // Convert to minutes
      }));
      
      setPerformanceData(chartData);
    } catch (err) {
      console.error('Failed to fetch performance trend:', err);
      // Fall back to the existing exam history approach
      console.warn('Performance trend endpoint failed, falling back to exam history');
      
      try {
        const filters = {
          ExamTypeId: selectedExamTypeId,
          IsCompleted: true
        };
        
        if (testType !== 'all') {
          filters.Mode = testType;
        }
        
        // Add date range filter based on timeframe
        if (timeframe !== 'all') {
          const days = parseInt(timeframe);
          const endDate = new Date();
          const startDate = new Date();
          startDate.setDate(startDate.getDate() - days);
          
          filters.StartDateFrom = startDate.toISOString();
          filters.StartDateTo = endDate.toISOString();
        }
        
        const history = await examService.getExamSessions(filters);
        setExamHistory(history || []);
        
        // Process performance data for charts using fallback method
        if (history && history.length > 0) {
          processPerformanceData(history);
        }
      } catch (fallbackErr) {
        console.error('Fallback exam history fetch also failed:', fallbackErr);
        setError('Failed to load performance data');
      }
    }
  }, [selectedExamTypeId, testType, timeframe, category, processPerformanceData]);

  // Fetch exam history (now used as fallback)
  const fetchExamHistory = useCallback(async () => {
    if (!selectedExamTypeId) return;
    
    try {
      const filters = {
        ExamTypeId: selectedExamTypeId,
        IsCompleted: true
      };
      
      if (testType !== 'all') {
        filters.Mode = testType;
      }
      
      // Add date range filter based on timeframe
      if (timeframe !== 'all') {
        const days = parseInt(timeframe);
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - days);
        
        filters.StartDateFrom = startDate.toISOString();
        filters.StartDateTo = endDate.toISOString();
      }
      
      const history = await examService.getExamSessions(filters);
      setExamHistory(history || []);
      
      // Process performance data for charts
      processPerformanceData(history || []);
    } catch (err) {
      console.error('Failed to fetch exam history:', err);
      setError('Failed to load exam history');
    }
  }, [selectedExamTypeId, testType, timeframe, category]);

  // Fetch category performance report
  const fetchCategoryReport = useCallback(async () => {
    if (!selectedExamTypeId) return;
    
    try {
      const options = {
        ExamTypeId: selectedExamTypeId
      };
      
      // Add date range filter
      if (timeframe !== 'all') {
        const days = parseInt(timeframe);
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - days);
        
        options.DateFrom = startDate;
        options.DateTo = endDate;
      }
      
      // Add exam mode filter (maps to testType)
      if (testType !== 'all') {
        options.ExamMode = testType;
      }
      
      // Add category filter
      if (category !== 'all') {
        options.CategoryIds = [parseInt(category)];
      }
      
      const report = await progressReportService.getTestByCategoryReport(options);
      setCategoryReport(report || []);
    } catch (err) {
      console.error('Failed to fetch category report:', err);
    }
  }, [selectedExamTypeId, timeframe, testType, category]);

  // Fetch performance summary from the new optimized endpoint
  const fetchPerformanceSummary = useCallback(async () => {
    if (!selectedExamTypeId) return;
    
    try {
      const options = {
        ExamTypeId: selectedExamTypeId
      };
      
      // Add date range filter
      if (timeframe !== 'all') {
        const days = parseInt(timeframe);
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - days);
        
        options.StartDateFrom = startDate;
        options.StartDateTo = endDate;
      }
      
      // Add exam mode filter (maps to testType)
      if (testType !== 'all') {
        options.ExamMode = testType;
      }
      
      // Add category filter
      if (category !== 'all') {
        options.CategoryIds = [parseInt(category)];
      }
      
      const summary = await progressReportService.getPerformanceSummary(options);
      
      // Validate and set the performance summary
      if (summary && typeof summary === 'object') {
        setPerformanceSummary({
          totalExams: summary.totalExams || 0,
          totalQuestions: summary.totalQuestions || 0,
          averageAccuracy: summary.averageAccuracy || 0,
          totalStudyTime: summary.totalStudyTimeHours || summary.totalStudyTime || 0,
          improvementTrend: summary.improvementTrend || 0,
          examsThisWeek: summary.examsThisWeek || 0,
          bestDailyScore: summary.bestDailyScore || 0,
          averageDailyQuestions: summary.averageDailyQuestions || 0,
          strongestCategory: summary.strongestCategory || '',
          weakestCategory: summary.weakestCategory || ''
        });
      } else {
        // Set default values if API response is invalid
        setPerformanceSummary({
          totalExams: 0,
          totalQuestions: 0,
          averageAccuracy: 0,
          totalStudyTime: 0,
          improvementTrend: 0,
          examsThisWeek: 0,
          bestDailyScore: 0,
          averageDailyQuestions: 0,
          strongestCategory: '',
          weakestCategory: ''
        });
      }
    } catch (err) {
      console.error('Failed to fetch performance summary:', err);
      // Set default values as fallback - the processPerformanceData function
      // can still calculate some metrics locally if needed
      setPerformanceSummary({
        totalExams: 0,
        totalQuestions: 0,
        averageAccuracy: 0,
        totalStudyTime: 0,
        improvementTrend: 0,
        examsThisWeek: 0,
        bestDailyScore: 0,
        averageDailyQuestions: 0,
        strongestCategory: '',
        weakestCategory: ''
      });
    }
  }, [selectedExamTypeId, timeframe, testType, category]);

  // Process category report data for performance charts when category filter is applied
  const processCategoryPerformanceData = useCallback(() => {
    if (!categoryReport || categoryReport.length === 0) {
      setPerformanceData([]);
      // Don't override performance summary if it came from the API endpoint
      return;
    }

    // Aggregate data from category report
    let totalQuestions = 0;
    let totalCorrect = 0;
    let totalTime = 0;
    
    categoryReport.forEach(catReport => {
      totalQuestions += catReport.totalQuestions || 0;
      totalCorrect += Math.round(((catReport.accuracyPercentage || 0) / 100) * (catReport.totalQuestions || 0));
      // Note: Category report may not have time data, so we'll estimate or use 0
    });

    // For category-filtered data, we'll create a simplified chart
    // Since category report doesn't have daily breakdown, we'll show aggregate data
    const chartData = [{
      name: 'Selected Category',
      totalQuestions,
      correctAnswers: totalCorrect,
      incorrectAnswers: totalQuestions - totalCorrect,
      accuracy: totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0,
      examCount: categoryReport.length,
      studyTime: 0 // Category report doesn't include time data
    }];
    
    setPerformanceData(chartData);
    
    // The performance summary is now handled by the API endpoint
    // This function now focuses only on chart data processing
  }, [categoryReport]);

  // Calculate improvement trend
  const calculateImprovementTrend = (data) => {
    // First try to get it from the performance summary API
    if (performanceSummary.improvementTrend !== undefined && performanceSummary.improvementTrend !== 0) {
      return performanceSummary.improvementTrend;
    }
    
    // Fallback to local calculation
    if (data.length < 2) return 0;
    
    const recent = data.slice(-7); // Last 7 days
    const earlier = data.slice(-14, -7); // Previous 7 days
    
    if (recent.length === 0 || earlier.length === 0) return 0;
    
    const recentAvg = recent.reduce((sum, day) => sum + day.accuracy, 0) / recent.length;
    const earlierAvg = earlier.reduce((sum, day) => sum + day.accuracy, 0) / earlier.length;
    
    return Math.round(recentAvg - earlierAvg);
  };
  // Find strongest category
  const findStrongestCategory = () => {
    // First try to get it from the performance summary API
    if (performanceSummary.strongestCategory && performanceSummary.strongestCategory !== '' && performanceSummary.strongestCategory !== 'N/A') {
      return performanceSummary.strongestCategory;
    }
    
    // Fallback to category report calculation
    if (categoryReport.length === 0) return 'N/A';
    
    // Calculate combined score: accuracy weight (70%) + progress weight (30%)
    const strongest = categoryReport.reduce((max, cat) => {
      const catScore = (cat.accuracyPercentage * 0.7) + (cat.completionPercentage * 0.3);
      const maxScore = (max.accuracyPercentage * 0.7) + (max.completionPercentage * 0.3);
      return catScore > maxScore ? cat : max;
    });
    
    return strongest.categoryName || 'N/A';
  };

  // Find weakest category
  const findWeakestCategory = () => {
    // First try to get it from the performance summary API
    if (performanceSummary.weakestCategory && performanceSummary.weakestCategory !== '' && performanceSummary.weakestCategory !== 'N/A') {
      return performanceSummary.weakestCategory;
    }
    
    // Fallback to category report calculation
    if (categoryReport.length === 0) return 'N/A';
    
    // Calculate combined score: accuracy weight (70%) + progress weight (30%)
    const weakest = categoryReport.reduce((min, cat) => {
      const catScore = (cat.accuracyPercentage * 0.7) + (cat.completionPercentage * 0.3);
      const minScore = (min.accuracyPercentage * 0.7) + (min.completionPercentage * 0.3);
      return catScore < minScore ? cat : min;
    });
    
    return weakest.categoryName || 'N/A';
  };

  // Handle exam type change
  const handleExamTypeChange = (selectedTypeName) => {
    const selectedType = examTypes.find(type => type.name === selectedTypeName);
    if (selectedType) {
      setSelectedExamTypeId(selectedType.id);
      setSelectedExamType(selectedType.name);
    }
  };

  // Initial data loading
  useEffect(() => {
    fetchExamTypes();
  }, [fetchExamTypes]);

  // Load data when exam type changes
  useEffect(() => {
    if (selectedExamTypeId) {
      fetchCategories();
    }
  }, [selectedExamTypeId, fetchCategories]);

  // Fetch all data
  useEffect(() => {
    const loadData = async () => {
      if (!selectedExamTypeId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        await Promise.all([
          fetchPerformanceTrend(),
          fetchCategoryReport(),
          fetchPerformanceSummary()
        ]);
      } catch (err) {
        console.error('Error loading performance data:', err);
        setError('Failed to load performance data');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [selectedExamTypeId, testType, timeframe, category, fetchPerformanceTrend, fetchCategoryReport, fetchPerformanceSummary]);

  // Process category data when category filter is applied and we have category report data
  useEffect(() => {
    // Only process category data if we have a specific category selected and category report data
    if (category !== 'all' && categoryReport.length > 0) {
      processCategoryPerformanceData();
    }
  }, [category, categoryReport, processCategoryPerformanceData]);
  
  // Calculate current study streak
  const calculateCurrentStreak = () => {
    if (performanceData.length === 0) return 0;
    
    const sortedData = [...performanceData].sort((a, b) => new Date(b.name) - new Date(a.name));
    let streak = 0;
    const today = new Date();
    
    for (let i = 0; i < sortedData.length; i++) {
      const dataDate = new Date(sortedData[i].name);
      const daysDiff = Math.floor((today - dataDate) / (1000 * 60 * 60 * 24));
      
      if (daysDiff === i && sortedData[i].totalQuestions > 0) {
        streak++;
      } else {
        break;
      }
    }
    
    return streak;
  };

  // Helper functions for chart data processing
  const getScoreDistribution = () => {
    const ranges = ['0-20%', '21-40%', '41-60%', '61-80%', '81-100%'];
    const distribution = ranges.map(range => ({ range, count: 0 }));
    
    performanceData.forEach(item => {
      const accuracy = item.accuracy || 0;
      if (accuracy <= 20) distribution[0].count++;
      else if (accuracy <= 40) distribution[1].count++;
      else if (accuracy <= 60) distribution[2].count++;
      else if (accuracy <= 80) distribution[3].count++;
      else distribution[4].count++;
    });
    
    return distribution;
  };

  const getWeeklyProgress = () => {
    const weeklyData = [];
    const now = new Date();
    
    for (let i = 4; i >= 0; i--) {
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - (i * 7));
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      
      const weekData = performanceData.filter(item => {
        const itemDate = new Date(item.date || item.name);
        return itemDate >= weekStart && itemDate <= weekEnd;
      });
      
      const totalQuestions = weekData.reduce((sum, item) => sum + (item.totalQuestions || 0), 0);
      const avgAccuracy = weekData.length > 0 
        ? weekData.reduce((sum, item) => sum + (item.accuracy || 0), 0) / weekData.length 
        : 0;
      
      weeklyData.push({
        week: `Week ${5-i}`,
        questions: totalQuestions,
        avgAccuracy: Math.round(avgAccuracy)
      });
    }
    
    return weeklyData;
  };

  // Helper functions for styling and colors
  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 85) return '#10b981'; // Green
    if (accuracy >= 70) return '#f59e0b'; // Yellow
    if (accuracy >= 50) return '#f97316'; // Orange
    return '#ef4444'; // Red
  };

  const getCompletionColor = (completion) => {
    if (completion >= 80) return '#10b981'; // Green
    if (completion >= 60) return '#3b82f6'; // Blue
    if (completion >= 40) return '#f59e0b'; // Yellow
    return '#ef4444'; // Red
  };

  // Study recommendation functions
  const getStudyRecommendation = () => {
    const avgAccuracy = performanceSummary.averageAccuracy;
    if (avgAccuracy < 50) return "Foundation Building";
    if (avgAccuracy < 70) return "Concept Reinforcement";
    if (avgAccuracy < 85) return "Practice Enhancement";
    return "Mastery & Testing";
  };

  const getDetailedRecommendation = () => {
    const avgAccuracy = performanceSummary.averageAccuracy;
    const totalExams = performanceSummary.totalExams;
    const recentTrend = performanceSummary.improvementTrend;
    
    if (avgAccuracy < 50) {
      return "Focus on reviewing basic concepts and theory before attempting more questions. Consider shorter, more frequent study sessions.";
    }
    if (avgAccuracy < 70) {
      return "Work on understanding underlying concepts. Mix review with practice questions, focusing on explanation analysis.";
    }
    if (avgAccuracy < 85) {
      return "Great foundation! Increase practice volume and try timed sessions to improve speed and accuracy.";
    }
    return "Excellent performance! Challenge yourself with simulated exams and focus on maintaining consistency.";
  };

  const getStudyPattern = () => {
    const recentDays = performanceData.slice(-7);
    const activeDays = recentDays.filter(day => day.totalQuestions > 0).length;
    const avgQuestionsPerDay = recentDays.reduce((sum, day) => sum + day.totalQuestions, 0) / Math.max(recentDays.length, 1);
    
    if (activeDays >= 6) return "Consistent Daily Practice";
    if (activeDays >= 4) return "Regular Study Schedule";
    if (activeDays >= 2) return "Moderate Activity";
    return "Irregular Pattern";
  };

  const getPatternAdvice = () => {
    const recentDays = performanceData.slice(-7);
    const activeDays = recentDays.filter(day => day.totalQuestions > 0).length;
    
    if (activeDays >= 6) {
      return "Excellent consistency! Consider adding rest days to avoid burnout.";
    }
    if (activeDays >= 4) {
      return "Good study rhythm. Try to maintain this pace for optimal results.";
    }
    if (activeDays >= 2) {
      return "Consider increasing study frequency for better retention and progress.";
    }
    return "Try to establish a more regular study schedule for better learning outcomes.";
  };

  const retakeIncorrectQuestions = () => {
    // Navigate to practice test with incorrect questions filter
    // This would need to be implemented based on your routing setup
  };

  const handleExpandClick = (chartName) => {
    setExpandedChart(chartName);
  };

  const handleCloseModal = () => {
    setExpandedChart(null);
  };

  // Theme-specific styles
  const themeStyles = useMemo(() => {
    return {
      container: {
        background: isDarkMode
          ? 'linear-gradient(180deg, #0e1626 0%, #0b1220 100%)'
          : '#eceff4'
      },
      text: {
        primary: isDarkMode ? '#eaf0f7' : '#2c3e50',
        secondary: isDarkMode ? '#9fb0c5' : 'rgba(44, 62, 80, 0.8)'
      },
      banner: {
        background: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.05)',
        border: isDarkMode ? '1px solid rgba(148, 163, 184, 0.12)' : '1px solid rgba(0, 0, 0, 0.05)'
      },
      select: {
        background: isDarkMode ? '#1c2740' : 'rgba(0, 0, 0, 0.1)',
        color: isDarkMode ? '#eaf0f7' : '#2c3e50'
      },
      chartBox: {
        background: isDarkMode ? '#151e30' : '#ffffff',
        shadow: isDarkMode ? '0 1px 2px rgba(0, 0, 0, 0.4), 0 8px 24px rgba(0, 0, 0, 0.28)' : '0 4px 10px rgba(0, 0, 0, 0.1)'
      },
      actionButton: {
        background: isDarkMode ? '#1c2740' : 'rgba(44, 62, 80, 0.1)',
        color: isDarkMode ? '#eaf0f7' : '#2c3e50'
      },
      gridLines: isDarkMode ? 'rgba(148, 163, 184, 0.12)' : 'rgba(0, 0, 0, 0.1)'
    };
  }, [isDarkMode]);

  const dropdownClass = isDarkMode ? 'dark-select' : 'light-select';

  return (
    <div className="performance-history-container" style={themeStyles.container}>
      {/* Welcome Banner */}
      <div className="welcome-banner" style={{
        background: themeStyles.banner.background,
        borderBottom: themeStyles.banner.border
      }}>
        <div className="welcome-info">
          <h2 style={{ color: themeStyles.text.primary }}>Performance History</h2>
          <p className="welcome-subtext" style={{ color: themeStyles.text.secondary }}>Track your progress over time</p>
        </div>
        <div className="current-date">
          <FontAwesomeIcon icon={faCalendarDay} className="calendar-icon" style={{ color: themeStyles.text.secondary }} />
          <span style={{ color: themeStyles.text.primary }}>{currentDate}</span>
        </div>
      </div>      <div className="dashboard-header">
        <h1 style={{ color: themeStyles.text.primary }}>Performance Analysis</h1>
        <div className="exam-type-selector">
          <span style={{ color: themeStyles.text.primary }}>Exam Type:</span>
          <select 
            id="exam-type" 
            value={selectedExamType} 
            onChange={(e) => handleExamTypeChange(e.target.value)}
            className={dropdownClass}
            disabled={loading}
          >
            {examTypes.length === 0 ? (
              <option value="">Loading exam types...</option>
            ) : (
              examTypes.map(type => (
                <option key={type.id} value={type.name}>
                  {type.name}
                </option>
              ))
            )}
          </select>
        </div>
      </div>

      {/* Performance Summary Cards */}
      <div className="performance-summary" style={{ marginBottom: '30px' }}>
        <div className="summary-cards" style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '20px',
          marginBottom: '20px'
        }}>
          <div className="summary-card" style={{
            background: themeStyles.chartBox.background,
            boxShadow: themeStyles.chartBox.shadow,
            padding: '20px',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <FontAwesomeIcon icon={faTrophy} style={{ 
              color: '#f59e0b', 
              fontSize: '24px', 
              marginBottom: '10px' 
            }} />
            <h3 style={{ color: themeStyles.text.primary, margin: '10px 0 5px 0' }}>
              {performanceSummary.totalExams}
            </h3>
            <p style={{ color: themeStyles.text.secondary, margin: 0 }}>Total Exams</p>
          </div>

          <div className="summary-card" style={{
            background: themeStyles.chartBox.background,
            boxShadow: themeStyles.chartBox.shadow,
            padding: '20px',
            borderRadius: '12px',
            textAlign: 'center'          }}>
            <FontAwesomeIcon icon={faBullseye} style={{ 
              color: '#10b981', 
              fontSize: '24px', 
              marginBottom: '10px' 
            }} />
            <h3 style={{ color: themeStyles.text.primary, margin: '10px 0 5px 0' }}>
              {performanceSummary.averageAccuracy}%
            </h3>
            <p style={{ color: themeStyles.text.secondary, margin: 0 }}>Average Accuracy</p>
          </div>

          <div className="summary-card" style={{
            background: themeStyles.chartBox.background,
            boxShadow: themeStyles.chartBox.shadow,
            padding: '20px',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <FontAwesomeIcon icon={faClock} style={{ 
              color: '#3b82f6', 
              fontSize: '24px', 
              marginBottom: '10px' 
            }} />
            <h3 style={{ color: themeStyles.text.primary, margin: '10px 0 5px 0' }}>
              {performanceSummary.totalStudyTime}h
            </h3>
            <p style={{ color: themeStyles.text.secondary, margin: 0 }}>Study Time</p>
          </div>          <div className="summary-card" style={{
            background: themeStyles.chartBox.background,
            boxShadow: themeStyles.chartBox.shadow,
            padding: '20px',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <FontAwesomeIcon icon={faBookOpen} style={{ 
              color: '#8b5cf6', 
              fontSize: '24px', 
              marginBottom: '10px' 
            }} />
            <h3 style={{ color: themeStyles.text.primary, margin: '10px 0 5px 0' }}>
              {performanceSummary.totalQuestions}
            </h3>
            <p style={{ color: themeStyles.text.secondary, margin: 0 }}>Questions Attempted</p>
          </div>
          
          {/* Recent Activity Card */}
          <div className="summary-card" style={{
            background: themeStyles.chartBox.background,
            boxShadow: themeStyles.chartBox.shadow,
            padding: '20px',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <FontAwesomeIcon icon={faClock} style={{ 
              color: '#14b8a6', 
              fontSize: '24px', 
              marginBottom: '10px' 
            }} />
            <h3 style={{ color: themeStyles.text.primary, margin: '10px 0 5px 0' }}>
              {performanceSummary.examsThisWeek > 0 
                ? performanceSummary.examsThisWeek
                : examHistory.filter(exam => {
                    const examDate = new Date(exam.startDate);
                    const weekAgo = new Date();
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    return examDate >= weekAgo;
                  }).length
              }
            </h3>
            <p style={{ color: themeStyles.text.secondary, margin: 0 }}>Exams This Week</p>
          </div>
          
          {/* Best Performance Card */}
          <div className="summary-card" style={{
            background: themeStyles.chartBox.background,
            boxShadow: themeStyles.chartBox.shadow,
            padding: '20px',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <FontAwesomeIcon icon={faTrophy} style={{ 
              color: '#eab308', 
              fontSize: '24px', 
              marginBottom: '10px' 
            }} />
            <h3 style={{ color: themeStyles.text.primary, margin: '10px 0 5px 0' }}>
              {performanceSummary.bestDailyScore > 0 
                ? `${performanceSummary.bestDailyScore.toFixed(1)}%`
                : `${Math.max(...performanceData.map(d => d.accuracy), 0)}%`
              }
            </h3>
            <p style={{ color: themeStyles.text.secondary, margin: 0 }}>Best Daily Score</p>
          </div>
          
          {/* Average Daily Questions Card */}
          {performanceSummary.averageDailyQuestions > 0 && (
            <div className="summary-card" style={{
              background: themeStyles.chartBox.background,
              boxShadow: themeStyles.chartBox.shadow,
              padding: '20px',
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <FontAwesomeIcon icon={faQuestionCircle} style={{ 
                color: '#06b6d4', 
                fontSize: '24px', 
                marginBottom: '10px' 
              }} />
              <h3 style={{ color: themeStyles.text.primary, margin: '10px 0 5px 0' }}>
                {Math.round(performanceSummary.averageDailyQuestions)}
              </h3>
              <p style={{ color: themeStyles.text.secondary, margin: 0 }}>Avg Daily Questions</p>
            </div>
          )}
        </div>

        {/* Performance Streak */}
        {performanceData.length > 0 && (
          <div style={{
            background: themeStyles.chartBox.background,
            boxShadow: themeStyles.chartBox.shadow,
            padding: '15px 20px',
            borderRadius: '8px',
            textAlign: 'center',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div>
              <span style={{ 
                color: themeStyles.text.primary,
                fontSize: '16px',
                fontWeight: 'bold'
              }}>
                🔥 Current Streak: {calculateCurrentStreak()} days
              </span>
              <p style={{ 
                color: themeStyles.text.secondary, 
                margin: '5px 0 0 0',
                fontSize: '14px'
              }}>
                Keep up the momentum!
              </p>
            </div>
          </div>
        )}

        {/* Improvement indicator */}
        {performanceSummary.improvementTrend !== 0 && (
          <div style={{
            background: themeStyles.chartBox.background,
            boxShadow: themeStyles.chartBox.shadow,
            padding: '15px 20px',
            borderRadius: '8px',
            textAlign: 'center',
            border: `2px solid ${performanceSummary.improvementTrend > 0 ? '#10b981' : '#ef4444'}`
          }}>
            <span style={{ 
              color: performanceSummary.improvementTrend > 0 ? '#10b981' : '#ef4444',
              fontSize: '18px',
              fontWeight: 'bold'
            }}>
              {performanceSummary.improvementTrend > 0 ? '↗️' : '↘️'} 
              {Math.abs(performanceSummary.improvementTrend)}% 
              {performanceSummary.improvementTrend > 0 ? ' improvement' : ' decline'} in recent performance
            </span>
          </div>
        )}
      </div>      <div className="filters">
        <div className="filter-group">
          <label style={{ color: themeStyles.text.primary }}>Test Type:</label>
          <select 
            value={testType} 
            onChange={e => setTestType(e.target.value)}
            className={dropdownClass}
            disabled={loading}
          >
            <option value="all">All Types</option>
            <option value="Practice">Practice Test</option>
            <option value="Simulated">Simulated Test</option>
          </select>
        </div>
        <div className="filter-group">
          <label style={{ color: themeStyles.text.primary }}>Timeframe:</label>
          <select 
            value={timeframe} 
            onChange={e => setTimeframe(e.target.value)}
            className={dropdownClass}
            disabled={loading}
          >
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="90">Last 3 Months</option>
            <option value="all">All Time</option>
          </select>
        </div>
      </div>

      {/* Loading and Error States */}
      {loading && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: themeStyles.text.primary 
        }}>
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          <p style={{ marginTop: '20px' }}>Loading performance data...</p>
        </div>
      )}

      {error && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: '#ef4444',
          background: 'rgba(239, 68, 68, 0.1)',
          borderRadius: '8px',
          margin: '20px 0'
        }}>
          <p>{error}</p>
        </div>
      )}      {!loading && !error && (
        <div className="dashboard-content">          <div className="charts-row">
            {/* Performance Trend Chart */}
            <div className="chart-box" style={{ 
              background: themeStyles.chartBox.background, 
              boxShadow: themeStyles.chartBox.shadow 
            }}>
              <div className="chart-header">
                <h3 style={{ color: themeStyles.text.primary }}>Performance Trend</h3>
                <FontAwesomeIcon 
                  icon={faInfoCircle} 
                  className="info-icon" 
                  title="Your accuracy trend over time" 
                  style={{ color: themeStyles.text.secondary }}
                />
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={performanceData}>
                  <CartesianGrid stroke={themeStyles.gridLines} />
                  <XAxis 
                    dataKey="name" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: themeStyles.text.primary }}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: themeStyles.text.primary }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: isDarkMode ? '#0f2132' : '#fff',
                      color: isDarkMode ? '#ffffff' : '#333',
                      border: isDarkMode ? '1px solid #2c5282' : '1px solid #ddd'
                    }}
                  />
                  <Line type="monotone" dataKey="accuracy" stroke="#10b981" strokeWidth={3} name="Accuracy %" />
                </LineChart>
              </ResponsiveContainer>
              <FontAwesomeIcon
                icon={faExpand}
                className="expand-icon"
                onClick={() => handleExpandClick('accuracy')}
                style={{ color: themeStyles.text.secondary }}
              />
            </div>

            {/* Study Volume & Time Chart */}
            <div className="chart-box" style={{ 
              background: themeStyles.chartBox.background, 
              boxShadow: themeStyles.chartBox.shadow 
            }}>
              <div className="chart-header">
                <h3 style={{ color: themeStyles.text.primary }}>Daily Study Volume</h3>
                <FontAwesomeIcon 
                  icon={faInfoCircle} 
                  className="info-icon" 
                  title="Questions attempted and time spent per day" 
                  style={{ color: themeStyles.text.secondary }}
                />
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={performanceData}>
                  <CartesianGrid stroke={themeStyles.gridLines} />
                  <XAxis 
                    dataKey="name" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: themeStyles.text.primary }}
                  />
                  <YAxis 
                    yAxisId="questions"
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: themeStyles.text.primary }}
                  />
                  <YAxis 
                    yAxisId="time"
                    orientation="right"
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: themeStyles.text.primary }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: isDarkMode ? '#0f2132' : '#fff',
                      color: isDarkMode ? '#ffffff' : '#333',
                      border: isDarkMode ? '1px solid #2c5282' : '1px solid #ddd'
                    }}
                  />
                  <Line 
                    yAxisId="questions"
                    type="monotone" 
                    dataKey="totalQuestions" 
                    stroke="#3b82f6" 
                    strokeWidth={2} 
                    name="Questions" 
                  />
                  <Line 
                    yAxisId="time"
                    type="monotone" 
                    dataKey="studyTime" 
                    stroke="#f59e0b" 
                    strokeWidth={2} 
                    name="Study Time (min)" 
                  />
                </LineChart>
              </ResponsiveContainer>
              <FontAwesomeIcon
                icon={faExpand}
                className="expand-icon"
                onClick={() => handleExpandClick('volume')}
                style={{ color: themeStyles.text.secondary }}
              />
            </div>          </div>

          {/* Advanced Performance Analytics */}
          <div className="charts-row" style={{ marginTop: '30px' }}>
            {/* Performance Distribution */}
            <div className="chart-box" style={{ 
              background: themeStyles.chartBox.background, 
              boxShadow: themeStyles.chartBox.shadow 
            }}>
              <div className="chart-header">
                <h3 style={{ color: themeStyles.text.primary }}>Score Distribution</h3>
                <FontAwesomeIcon 
                  icon={faInfoCircle} 
                  className="info-icon" 
                  title="Distribution of your daily accuracy scores" 
                  style={{ color: themeStyles.text.secondary }}
                />
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={getScoreDistribution()}>
                  <CartesianGrid stroke={themeStyles.gridLines} />
                  <XAxis 
                    dataKey="range" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: themeStyles.text.primary }}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: themeStyles.text.primary }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: isDarkMode ? '#0f2132' : '#fff',
                      color: isDarkMode ? '#ffffff' : '#333',
                      border: isDarkMode ? '1px solid #2c5282' : '1px solid #ddd'
                    }}
                  />
                  <Bar dataKey="count" fill="#8b5cf6" name="Days" />
                </BarChart>
              </ResponsiveContainer>
              <FontAwesomeIcon
                icon={faExpand}
                className="expand-icon"
                onClick={() => handleExpandClick('distribution')}
                style={{ color: themeStyles.text.secondary }}
              />
            </div>

            {/* Weekly Progress */}
            <div className="chart-box" style={{ 
              background: themeStyles.chartBox.background, 
              boxShadow: themeStyles.chartBox.shadow 
            }}>
              <div className="chart-header">
                <h3 style={{ color: themeStyles.text.primary }}>Weekly Progress</h3>
                <FontAwesomeIcon 
                  icon={faInfoCircle} 
                  className="info-icon" 
                  title="Your study progress by week" 
                  style={{ color: themeStyles.text.secondary }}
                />
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={getWeeklyProgress()}>
                  <CartesianGrid stroke={themeStyles.gridLines} />
                  <XAxis 
                    dataKey="week" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: themeStyles.text.primary }}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: themeStyles.text.primary }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: isDarkMode ? '#0f2132' : '#fff',
                      color: isDarkMode ? '#ffffff' : '#333',
                      border: isDarkMode ? '1px solid #2c5282' : '1px solid #ddd'
                    }}
                  />
                  <Bar dataKey="questions" fill="#06b6d4" name="Questions" />
                  <Bar dataKey="avgAccuracy" fill="#10b981" name="Avg Accuracy %" />
                </BarChart>
              </ResponsiveContainer>
              <FontAwesomeIcon
                icon={faExpand}
                className="expand-icon"
                onClick={() => handleExpandClick('weekly')}
                style={{ color: themeStyles.text.secondary }}
              />
            </div>
          </div>

          <div className="charts-row">
            <div className="chart-box" style={{ 
              background: themeStyles.chartBox.background, 
              boxShadow: themeStyles.chartBox.shadow 
            }}>
              <div className="chart-header">
                <h3 style={{ color: themeStyles.text.primary }}>Correct vs Incorrect</h3>
                <FontAwesomeIcon 
                  icon={faInfoCircle} 
                  className="info-icon" 
                  title="Breakdown of correct and incorrect answers" 
                  style={{ color: themeStyles.text.secondary }}
                />
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={performanceData}>
                  <CartesianGrid stroke={themeStyles.gridLines} />
                  <XAxis 
                    dataKey="name" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: themeStyles.text.primary }}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: themeStyles.text.primary }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: isDarkMode ? '#0f2132' : '#fff',
                      color: isDarkMode ? '#ffffff' : '#333',
                      border: isDarkMode ? '1px solid #2c5282' : '1px solid #ddd'
                    }}
                  />
                  <Bar dataKey="correctAnswers" stackId="a" fill="#10b981" name="Correct" />
                  <Bar dataKey="incorrectAnswers" stackId="a" fill="#ef4444" name="Incorrect" />
                </BarChart>
              </ResponsiveContainer>
              <FontAwesomeIcon
                icon={faExpand}
                className="expand-icon"
                onClick={() => handleExpandClick('breakdown')}
                style={{ color: themeStyles.text.secondary }}
              />
            </div>

            <div className="chart-box" style={{ 
              background: themeStyles.chartBox.background, 
              boxShadow: themeStyles.chartBox.shadow 
            }}>
              <div className="chart-header">
                <h3 style={{ color: themeStyles.text.primary }}>Category Performance</h3>
                <FontAwesomeIcon 
                  icon={faInfoCircle} 
                  className="info-icon" 
                  title="Performance breakdown by category" 
                  style={{ color: themeStyles.text.secondary }}
                />
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={categoryReport.slice(0, 8).map((cat, index) => ({
                      name: cat.categoryName,
                      value: cat.accuracyPercentage,
                      fill: [
                        '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
                        '#8b5cf6', '#06b6d4', '#84cc16', '#f97316'
                      ][index % 8]
                    }))}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${entry.value}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  />
                  <Tooltip 
                    formatter={(value, name) => {
                      const textColor = isDarkMode ? '#ffffff' : '#333';
                      return [
                        <span style={{ color: textColor }}>{`${value}%`}</span>,
                        <span style={{ color: textColor }}>{name}</span>
                      ];
                    }}
                    contentStyle={{ 
                      backgroundColor: isDarkMode ? '#0f2132' : '#fff',
                      border: isDarkMode ? '1px solid #2c5282' : '1px solid #ddd'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <FontAwesomeIcon
                icon={faExpand}
                className="expand-icon"
                onClick={() => handleExpandClick('category')}
                style={{ color: themeStyles.text.secondary }}
              />
            </div>
          </div>          {/* Enhanced Category Performance Table */}
          {categoryReport.length > 0 && (
            <div className="chart-box" style={{ 
              background: themeStyles.chartBox.background, 
              boxShadow: themeStyles.chartBox.shadow,
              marginTop: '20px'
            }}>
              <div className="chart-header">
                <h3 style={{ color: themeStyles.text.primary }}>Detailed Category Analysis</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <FontAwesomeIcon 
                    icon={faInfoCircle} 
                    className="info-icon" 
                    title="Click on category names to drill down" 
                    style={{ color: themeStyles.text.secondary }}
                  />
                  <select 
                    style={{
                      background: themeStyles.select.background,
                      color: themeStyles.select.color,
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '12px'
                    }}
                    onChange={(e) => {
                      // Sort categories by selected criteria
                      const sortBy = e.target.value;
                      let sorted = [...categoryReport];
                      if (sortBy === 'accuracy') {
                        sorted.sort((a, b) => b.accuracyPercentage - a.accuracyPercentage);
                      } else if (sortBy === 'questions') {
                        sorted.sort((a, b) => b.totalQuestions - a.totalQuestions);
                      } else if (sortBy === 'completion') {
                        sorted.sort((a, b) => b.completionPercentage - a.completionPercentage);
                      } else {
                        sorted.sort((a, b) => a.categoryName.localeCompare(b.categoryName));
                      }
                      setCategoryReport(sorted);
                    }}
                  >
                    <option value="name">Sort by Name</option>
                    <option value="accuracy">Sort by Accuracy</option>
                    <option value="questions">Sort by Questions</option>
                    <option value="completion">Sort by Completion</option>
                  </select>
                </div>
              </div>
              <div style={{ overflowX: 'auto', padding: '20px' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse',
                  color: themeStyles.text.primary
                }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${themeStyles.gridLines}` }}>
                      <th style={{ textAlign: 'left', padding: '12px 8px', position: 'relative' }}>
                        Category
                        <div style={{ 
                          position: 'absolute',
                          bottom: '2px',
                          left: '8px',
                          right: '8px',
                          height: '2px',
                          background: 'linear-gradient(90deg, #10b981, #3b82f6)',
                          borderRadius: '1px'
                        }} />
                      </th>
                      <th style={{ textAlign: 'center', padding: '12px 8px' }}>Total</th>
                      <th style={{ textAlign: 'center', padding: '12px 8px' }}>Attempted</th>
                      <th style={{ textAlign: 'center', padding: '12px 8px' }}>Correct</th>
                      <th style={{ textAlign: 'center', padding: '12px 8px' }}>Accuracy</th>
                      <th style={{ textAlign: 'center', padding: '12px 8px' }}>Progress</th>
                      <th style={{ textAlign: 'center', padding: '12px 8px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryReport.map((cat, index) => (
                      <tr key={cat.categoryId} style={{ 
                        borderBottom: `1px solid ${themeStyles.gridLines}`,
                        backgroundColor: index % 2 === 0 ? 'transparent' : (isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'),
                        transition: 'background-color 0.2s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'transparent' : (isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)');
                      }}
                      >
                        <td style={{ 
                          padding: '16px 8px', 
                          fontWeight: '600',
                          borderLeft: `4px solid ${getAccuracyColor(cat.accuracyPercentage)}`,
                          background: `linear-gradient(90deg, ${getAccuracyColor(cat.accuracyPercentage)}08, transparent 20%)`
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>{cat.categoryName}</span>
                            {cat.accuracyPercentage >= 85 && <span>🏆</span>}
                            {cat.accuracyPercentage < 50 && <span>⚠️</span>}
                          </div>
                        </td>
                        <td style={{ textAlign: 'center', padding: '16px 8px', fontWeight: '500' }}>
                          {cat.totalQuestions}
                        </td>
                        <td style={{ textAlign: 'center', padding: '16px 8px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <span style={{ fontWeight: '500' }}>{cat.questionsAnswered}</span>
                            <div style={{
                              width: '40px',
                              height: '4px',
                              background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                              borderRadius: '2px',
                              overflow: 'hidden',
                              marginTop: '4px'
                            }}>
                              <div style={{
                                width: `${(cat.questionsAnswered / cat.totalQuestions) * 100}%`,
                                height: '100%',
                                background: '#3b82f6',
                                borderRadius: '2px'
                              }} />
                            </div>
                          </div>
                        </td>
                        <td style={{ textAlign: 'center', padding: '16px 8px', fontWeight: '500' }}>
                          {cat.correctAnswers}
                        </td>
                        <td style={{ 
                          textAlign: 'center', 
                          padding: '16px 8px',
                          fontWeight: 'bold'
                        }}>
                          <div style={{ 
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '4px 8px',
                            borderRadius: '12px',
                            background: `${getAccuracyColor(cat.accuracyPercentage)}20`,
                            color: getAccuracyColor(cat.accuracyPercentage)
                          }}>
                            {cat.accuracyPercentage.toFixed(1)}%
                            {cat.accuracyPercentage >= 85 ? '📈' : cat.accuracyPercentage >= 70 ? '📊' : '📉'}
                          </div>
                        </td>
                        <td style={{ textAlign: 'center', padding: '16px 8px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                            <span style={{ fontSize: '12px', color: themeStyles.text.secondary }}>
                              {cat.completionPercentage.toFixed(0)}%
                            </span>
                            <div style={{
                              width: '50px',
                              height: '6px',
                              background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                              borderRadius: '3px',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                width: `${cat.completionPercentage}%`,
                                height: '100%',
                                background: `linear-gradient(90deg, ${getCompletionColor(cat.completionPercentage)}, ${getCompletionColor(cat.completionPercentage)}80)`,
                                borderRadius: '3px',
                                transition: 'width 0.3s ease'
                              }} />
                            </div>
                          </div>
                        </td>
            <td style={{ textAlign: 'center', padding: '16px 8px' }}>                          <button
              className="ph-action-btn"
                            onClick={() => {
                              // Navigate to practice test with this category pre-selected
                              navigate(`/practice-test?category=${encodeURIComponent(cat.categoryName)}&examType=${encodeURIComponent(selectedExamType)}`);
                            }}
                            style={{
                              // Use distinct colors for Practice vs Review in light mode
                              // Dark mode keeps prior semantics (red for Practice, green for Review)
                              background: isDarkMode
                                ? (cat.accuracyPercentage < 70 ? '#ef4444' : '#10b981')
                                : (cat.accuracyPercentage < 70 ? '#3b82f6' : '#16a34a'),
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '6px 12px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.opacity = '0.8';
                              e.target.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.opacity = '1';
                              e.target.style.transform = 'scale(1)';
                            }}
                          >
                            {cat.accuracyPercentage < 70 ? 'Practice' : 'Review'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}{/* Enhanced Performance Insights */}
          <div className="performance-insights" style={{ marginTop: '30px' }}>
            <div className="chart-box" style={{ 
              background: themeStyles.chartBox.background, 
              boxShadow: themeStyles.chartBox.shadow,
              padding: '25px'
            }}>
              <h3 style={{ color: themeStyles.text.primary, marginBottom: '20px' }}>Performance Insights & Recommendations</h3>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
                gap: '20px' 
              }}>
                <div style={{ 
                  padding: '20px', 
                  borderLeft: `4px solid #10b981`,
                  background: isDarkMode ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.05)',
                  borderRadius: '8px'
                }}>
                  <h4 style={{ color: themeStyles.text.primary, margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FontAwesomeIcon icon={faTrophy} style={{ color: '#10b981' }} />
                    Strongest Area
                  </h4>                  <p style={{ color: themeStyles.text.secondary, margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>
                    {findStrongestCategory()}
                  </p>
                  <p style={{ color: themeStyles.text.secondary, margin: 0, fontSize: '14px' }}>
                    {categoryReport.find(cat => cat.categoryName === findStrongestCategory())?.accuracyPercentage ? 
                      `${categoryReport.find(cat => cat.categoryName === findStrongestCategory()).accuracyPercentage.toFixed(1)}% accuracy` : 
                      'Keep up the excellent work!'
                    }
                  </p>
                </div>
                
                <div style={{ 
                  padding: '20px', 
                  borderLeft: `4px solid #ef4444`,
                  background: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)',
                  borderRadius: '8px'
                }}>                  <h4 style={{ color: themeStyles.text.primary, margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FontAwesomeIcon icon={faBullseye} style={{ color: '#ef4444' }} />
                    Needs Improvement
                  </h4>                  <p style={{ color: themeStyles.text.secondary, margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>
                    {findWeakestCategory()}
                  </p>
                  <p style={{ color: themeStyles.text.secondary, margin: 0, fontSize: '14px' }}>
                    {categoryReport.find(cat => cat.categoryName === findWeakestCategory())?.accuracyPercentage ? 
                      `${categoryReport.find(cat => cat.categoryName === findWeakestCategory()).accuracyPercentage.toFixed(1)}% accuracy - Focus here for improvement` : 
                      'Focus your study efforts on this area'
                    }
                  </p>
                </div>
                
                <div style={{ 
                  padding: '20px', 
                  borderLeft: `4px solid #3b82f6`,
                  background: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
                  borderRadius: '8px'
                }}>
                  <h4 style={{ color: themeStyles.text.primary, margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FontAwesomeIcon icon={faBookOpen} style={{ color: '#3b82f6' }} />
                    Study Recommendation
                  </h4>
                  <p style={{ color: themeStyles.text.secondary, margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>
                    {getStudyRecommendation()}
                  </p>
                  <p style={{ color: themeStyles.text.secondary, margin: 0, fontSize: '14px' }}>
                    {getDetailedRecommendation()}
                  </p>
                </div>
                
                <div style={{ 
                  padding: '20px', 
                  borderLeft: `4px solid #8b5cf6`,
                  background: isDarkMode ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.05)',
                  borderRadius: '8px'
                }}>
                  <h4 style={{ color: themeStyles.text.primary, margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FontAwesomeIcon icon={faClock} style={{ color: '#8b5cf6' }} />
                    Study Pattern
                  </h4>
                  <p style={{ color: themeStyles.text.secondary, margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>
                    {getStudyPattern()}
                  </p>
                  <p style={{ color: themeStyles.text.secondary, margin: 0, fontSize: '14px' }}>
                    {getPatternAdvice()}
                  </p>
                </div>
              </div>
              
              {/* Progress Goals */}
              <div style={{ 
                marginTop: '25px',
                padding: '20px',
                background: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                borderRadius: '8px',
                border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`
              }}>
                <h4 style={{ color: themeStyles.text.primary, margin: '0 0 15px 0' }}>
                  🎯 Progress Goals
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                      <span style={{ color: themeStyles.text.secondary, fontSize: '14px' }}>Weekly Target</span>
                      <span style={{ color: themeStyles.text.primary, fontSize: '14px', fontWeight: '600' }}>
                        {examHistory.filter(exam => {
                          const examDate = new Date(exam.startDate);
                          const weekAgo = new Date();
                          weekAgo.setDate(weekAgo.getDate() - 7);
                          return examDate >= weekAgo;
                        }).length}/5 exams
                      </span>
                    </div>
                    <div style={{ 
                      background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                      height: '8px',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        background: '#10b981',
                        height: '100%',
                        width: `${Math.min(100, (examHistory.filter(exam => {
                          const examDate = new Date(exam.startDate);
                          const weekAgo = new Date();
                          weekAgo.setDate(weekAgo.getDate() - 7);
                          return examDate >= weekAgo;
                        }).length / 5) * 100)}%`,
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  </div>
                  
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                      <span style={{ color: themeStyles.text.secondary, fontSize: '14px' }}>Accuracy Goal</span>
                      <span style={{ color: themeStyles.text.primary, fontSize: '14px', fontWeight: '600' }}>
                        {performanceSummary.averageAccuracy}/80%
                      </span>
                    </div>
                    <div style={{ 
                      background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                      height: '8px',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        background: performanceSummary.averageAccuracy >= 80 ? '#10b981' : '#f59e0b',
                        height: '100%',
                        width: `${Math.min(100, (performanceSummary.averageAccuracy / 80) * 100)}%`,
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}      {/* Action Buttons */}
      <div className="performance-actions" style={{ 
        marginTop: '30px',
        display: 'flex',
        gap: '15px',
        flexWrap: 'wrap',
        justifyContent: 'center',
        padding: '20px'
      }}>        <button 
          className="action-button practice-button" 
          onClick={() => {
            const weakestCategory = findWeakestCategory();
            if (weakestCategory && weakestCategory !== 'N/A') {
              navigate(`/practice-test?category=${encodeURIComponent(weakestCategory)}&examType=${encodeURIComponent(selectedExamType)}`);
            } else {
              navigate('/practice-test');
            }
          }}
          style={{ 
            backgroundColor: '#ef4444',
            color: 'white',
            padding: '12px 24px',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >          <FontAwesomeIcon icon={faBullseye} />
          Practice Weak Areas
        </button>
          <button 
          className="action-button" 
          onClick={() => navigate('/practice-test')}
          style={{ 
            backgroundColor: '#3b82f6',
            color: 'white',
            padding: '12px 24px',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <FontAwesomeIcon icon={faBookOpen} />
          Start Practice Session
        </button>
        
        <button 
          className="action-button" 
          onClick={() => navigate('/simulate-exam')}
          style={{ 
            backgroundColor: '#10b981',
            color: 'white',
            padding: '12px 24px',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <FontAwesomeIcon icon={faTrophy} />
          Take Simulated Exam        </button>
      </div>      <Modal
        show={!!expandedChart}
        onClose={handleCloseModal}
        chartData={performanceData}
        chartType={expandedChart}
        isDarkMode={isDarkMode}
        categoryReport={categoryReport}
        getScoreDistribution={getScoreDistribution}
        getWeeklyProgress={getWeeklyProgress}
      />
      <Footer />
    </div>
  );
}
